// Feature flags
export const ENABLE_MODIFIER_POPOVER = true;

// Timing constants
export const HOTKEY_HOLD_DURATION_MS = 300; // Provides good balance between accidental triggers and responsiveness

// Safe access to process.env for browser environments where process may not exist
export const getEnvVar = (key: string): string | undefined => {
  try {
    return typeof process !== "undefined" && process.env ? process.env[key] : undefined;
  } catch {
    return undefined;
  }
};

/** Check if running in development mode */
export const isDevelopmentMode = (): boolean => {
  return getEnvVar("NODE_ENV") !== "production";
};

/**
 * LAYER 1: Build-Time Environment Flag (Master Kill Switch)
 *
 * Master feature flag for HotKeys system - controls whether the HotKeys system
 * is available AT ALL in the application.
 *
 * @default false (disabled in production for safety)
 *
 * Enable Methods:
 * - Set VITE_ENABLE_HOTKEYS=true in .env.local
 * - Set ENABLE_HOTKEYS=true in build environment
 * - Development/staging environments auto-enable
 * - Localhost auto-enables (10.211.x.x VM development)
 *
 * Runtime Override:
 * - Set window.__ENABLE_HOTKEYS__ = true in browser console
 *
 * SAFETY: Defaults to FALSE to prevent accidental production rollout.
 */
export const HOTKEYS_FEATURE_ENABLED = (() => {
  // Explicit environment variable (highest priority)
  if (getEnvVar("VITE_ENABLE_HOTKEYS") === "true" || getEnvVar("ENABLE_HOTKEYS") === "true") {
    return true;
  }

  // Runtime override for production debugging (set in browser console)
  if (typeof window !== "undefined" && (window as any).__ENABLE_HOTKEYS__ === true) {
    console.warn("[HotKeys] Runtime override enabled via window.__ENABLE_HOTKEYS__");
    return true;
  }

  // Auto-enable in development/staging environments (NOT production)
  const nodeEnv = getEnvVar("NODE_ENV");
  const viteEnv = getEnvVar("VITE_ENVIRONMENT");
  if (nodeEnv !== "production" || (viteEnv !== "prod" && viteEnv !== "production")) {
    return true;
  }

  // Auto-enable on localhost/VM development
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname.startsWith("10.211") || hostname.startsWith("127.0")) {
      return true;
    }
  }

  // Default: DISABLED (safe default for production)
  return false;
})();

export const DEFAULT_COMPONENT_CONFIG = {
  commandPalette: {
    maxResults: 50,
    debounceDelay: 200,
    recentCommandsLimit: 10,
  },
  helpDialog: {
    defaultTitle: "Keyboard Shortcuts",
  },
  badge: {
    defaultSize: "sm" as const,
    defaultTheme: "default" as const,
    defaultPlacement: "after" as const,
  },
  modifierPopover: {
    holdDuration: HOTKEY_HOLD_DURATION_MS,
    position: "bottom-left" as const,
    maxItems: 24,
  },
};

export const COMPONENT_CLASSES = {
  badge: {
    base: "hotkey-badge",
    size: {
      xs: "hotkey-badge--xs",
      sm: "hotkey-badge--sm",
      md: "hotkey-badge--md",
      lg: "hotkey-badge--lg",
    },
    theme: {
      default: "hotkey-badge--default",
      minimal: "hotkey-badge--minimal",
      prominent: "hotkey-badge--prominent",
      ghost: "hotkey-badge--ghost",
      neon: "hotkey-badge--neon",
      glass: "hotkey-badge--glass",
    },
    placement: {
      before: "hotkey-badge--before",
      after: "hotkey-badge--after",
      append: "hotkey-badge--append",
      prepend: "hotkey-badge--prepend",
    },
    mobile: "hotkey-badge--mobile-hidden",
    disabled: "hotkey-badge--disabled",
  },
  palette: {
    overlay: "command-palette-overlay",
    container: "command-palette",
    input: "command-palette__input",
    results: "command-palette__results",
    category: "command-palette__category",
    command: "command-palette__command",
    commandSelected: "command-palette__command--selected",
    icon: "command-palette__icon",
    shortcut: "command-palette__shortcut",
    empty: "command-palette__empty",
  },
  helpDialog: {
    container: "help-dialog",
    header: "help-dialog__header",
    search: "help-dialog__search",
    content: "help-dialog__content",
    category: "help-dialog__category",
    shortcut: "help-dialog__shortcut",
    empty: "help-dialog__empty",
  },
  modifierPopover: {
    container: "modifier-popover",
    position: {
      "bottom-left": "modifier-popover--bottom-left",
      "bottom-right": "modifier-popover--bottom-right",
      cursor: "modifier-popover--cursor",
    },
    header: "modifier-popover__header",
    headerText: "modifier-popover__header-text",
    modifierKey: "modifier-popover__modifier-key",
    list: "modifier-popover__list",
    item: "modifier-popover__item",
    itemContent: "modifier-popover__item-content",
    itemTitle: "modifier-popover__item-title",
    itemCategory: "modifier-popover__item-category",
    footer: "modifier-popover__footer",
  },
};

export const NAVIGATION_KEYS = {
  UP: "ArrowUp",
  DOWN: "ArrowDown",
  LEFT: "ArrowLeft",
  RIGHT: "ArrowRight",
  ENTER: "Enter",
  ESCAPE: "Escape",
  TAB: "Tab",
  HOME: "Home",
  END: "End",
  PAGE_UP: "PageUp",
  PAGE_DOWN: "PageDown",
};

export const BADGE_THEMES = ["default", "minimal", "prominent", "ghost", "neon", "glass"] as const;

export const BADGE_PLACEMENTS = ["before", "after", "append", "prepend"] as const;

export const Z_INDEX = {
  commandPalette: 9999,
  helpDialog: 9998,
  tooltip: 9997,
};

export const ANIMATION_DURATION = {
  fast: 150,
  normal: 200,
  slow: 300,
};

export const DEBOUNCE_DELAY = {
  search: 200,
  keyboard: 50,
};

export const DISPLAY_NAMES = {
  HotKeyRoot: "HotKeyRoot",
  HotKey: "HotKey",
  HotkeyBadge: "HotkeyBadge",
  CommandPalette: "CommandPalette",
  HelpDialog: "HelpDialog",
  HotkeyProvider: "HotkeyProvider",
  ModifierPopover: "ModifierPopover",
  DebugOverlay: "DebugOverlay",
};
